import { useCallback } from 'react'
import { useReactFlow } from 'reactflow'
import type { ReactFlowCanvasNodeData } from '../../../../hooks/useYCanvas'
import { createNadDebug } from '../../../../utils/debugLog'

const selectLog = createNadDebug('dw:large-canvas-performance')
const SELECT_WARN_NODE_COUNT = 100

/**
 * Select exactly one node without replacing every node object in ReactFlow state.
 *
 * Several quick-info buttons open bottom panels by forcing their node selected.
 * Replacing all node objects on a large canvas causes broad node re-renders during
 * the panel animation; preserve object identity for nodes whose selected bit is
 * already correct.
 */
export function useSelectSingleCanvasNode(): (nodeId: string) => void {
  const { setNodes } = useReactFlow<ReactFlowCanvasNodeData>()

  return useCallback((nodeId: string) => {
    setNodes((nodes) => {
      let changed = false
      let changedNodeCount = 0
      const nextNodes = nodes.map((node) => {
        const shouldSelect = node.id === nodeId
        if (Boolean(node.selected) === shouldSelect) return node
        changed = true
        changedNodeCount += 1
        return { ...node, selected: shouldSelect }
      })

      if (!changed) return nodes

      if (nodes.length >= SELECT_WARN_NODE_COUNT) {
        selectLog({
          event: 'single-node-selection',
          node_id: nodeId,
          node_count: nodes.length,
          changed_node_count: changedNodeCount,
        })
      }

      return nextNodes
    })
  }, [setNodes])
}
